/* API client for the kiosk analytics suite (Self Service Orders → Insights).
 *
 * Every endpoint returns {success, data}; these helpers unwrap to `data` and
 * throw on failure, so screens can use try/catch rather than checking a flag.
 */
import apiClient from './api';

const base = (restaurantId) => `/users/restaurant/${restaurantId}`;

async function get(url, params) {
  const { data } = await apiClient.get(url, { params });
  if (!data?.success) throw new Error(data?.error || 'Request failed');
  return data.data;
}

// ---------------------------------------------------------------- festivals
export const getFestivals = (rid) => get(`${base(rid)}/festivals/`);

export const getCalendar = (rid, { year, locationId } = {}) =>
  get(`${base(rid)}/festivals/calendar/`, { year, location_id: locationId || undefined });

export const getRanking = (rid, { year, locationId } = {}) =>
  get(`${base(rid)}/festivals/ranking/`, { year, location_id: locationId || undefined });

export const getAnnual = (rid, { year, locationId } = {}) =>
  get(`${base(rid)}/festivals/annual/`, { year, location_id: locationId || undefined });

export const getForecast = (rid, { locationId } = {}) =>
  get(`${base(rid)}/festivals/forecast/`, { location_id: locationId || undefined });

export const getFestivalComparison = (rid, dateId, { locationId } = {}) =>
  get(`${base(rid)}/festivals/dates/${dateId}/compare/`, { location_id: locationId || undefined });

/* Fill this restaurant's calendar from the published Mauritian tables. The
   bulk seeder only runs at server bootstrap, so a restaurant created after it
   opens on a blank year; this is the door out of that. Idempotent server-side,
   so pressing it twice is harmless. */
export async function seedFestivals(rid) {
  const { data } = await apiClient.post(`${base(rid)}/festivals/seed/`);
  if (!data?.success) throw new Error(data?.error || 'Could not load the calendar');
  return data.data;
}

export async function createFestival(rid, body) {
  const { data } = await apiClient.post(`${base(rid)}/festivals/`, body);
  if (!data?.success) throw new Error(data?.error || 'Could not save the festival');
  return data.data;
}

export async function updateFestival(rid, festivalId, body) {
  const { data } = await apiClient.patch(`${base(rid)}/festivals/${festivalId}/`, body);
  if (!data?.success) throw new Error(data?.error || 'Could not update the festival');
  return data.data;
}

export async function deleteFestival(rid, festivalId) {
  const { data } = await apiClient.delete(`${base(rid)}/festivals/${festivalId}/`);
  if (!data?.success) throw new Error(data?.error || 'Could not delete the festival');
  return data.data;
}

export async function addFestivalDate(rid, festivalId, body) {
  const { data } = await apiClient.post(`${base(rid)}/festivals/${festivalId}/dates/`, body);
  if (!data?.success) throw new Error(data?.error || 'Could not add the date');
  return data.data;
}

export async function updateFestivalDate(rid, dateId, body) {
  const { data } = await apiClient.patch(`${base(rid)}/festivals/dates/${dateId}/`, body);
  if (!data?.success) throw new Error(data?.error || 'Could not update the date');
  return data.data;
}

export async function deleteFestivalDate(rid, dateId) {
  const { data } = await apiClient.delete(`${base(rid)}/festivals/dates/${dateId}/`);
  if (!data?.success) throw new Error(data?.error || 'Could not remove the date');
  return data.data;
}

/** Rows → CSV download. Shared by every report's export control. */
export function exportCsv(filename, rows) {
  if (!rows || !rows.length) return;
  const cols = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
